import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import StatusBadge from "../../components/StatusBadge";
import { formatDateTime, resolveImageUrl } from "../../lib/format";

export default function MyLicenses() {
    const [licenses, setLicenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/me/licenses");
            setLicenses(data || []);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const copyKey = (key) => {
        navigator.clipboard.writeText(key);
        setCopied(key);
        toast.success("License copied to clipboard");
        setTimeout(() => setCopied(""), 1500);
    };

    const deactivate = async (licenseId, activationId, machineName) => {
        if (!window.confirm(`Release the machine "${machineName}"? You can activate another one in its place.`)) return;
        try {
            await api.post(`/me/licenses/${licenseId}/deactivate`, { activation_id: activationId });
            toast.success("Machine deactivated");
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    return (
        <DashboardLayout role="client">
            <div className="mb-8" data-testid="my-licenses-page">
                <div className="label-mono mb-3">/ My Licenses</div>
                <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Activation licenses</h1>
                <p className="text-avx-muted mt-2">Each purchase generates a license that you can activate on multiple machines. Use the AVX Pro desktop client to install and activate your plugins.</p>
            </div>

            {loading ? (
                <div className="text-avx-muted font-mono">Loading…</div>
            ) : licenses.length === 0 ? (
                <div className="panel p-10 text-center text-avx-muted">
                    You don't have any licenses yet. When a purchase is marked as paid, it will appear here automatically.
                </div>
            ) : (
                <div className="space-y-4" data-testid="licenses-list">
                    {licenses.map((lic) => {
                        const isTrial = lic.is_trial || lic.kind === "trial";
                        const expired = lic.is_expired;
                        const days = lic.days_remaining;
                        // Visual "active" state must reflect BOTH the DB status and expiration.
                        // Even if the periodic sweep hasn't updated status yet, an expired trial
                        // must render as revoked/cancelled to avoid misleading the customer.
                        const visuallyActive = lic.status === "active" && !expired;
                        return (
                        <div key={lic.id} className="panel-elevated p-6" data-testid={`license-card-${lic.id}`}>
                            <div className="flex items-start justify-between gap-4 mb-4">
                                <div className="flex items-center gap-4">
                                    {lic.product?.image_url ? (
                                        <img src={resolveImageUrl(lic.product.image_url)} alt="" className="w-16 h-16 object-cover rounded-sm border border-avx-line" />
                                    ) : (
                                        <div className="w-16 h-16 rounded-sm bg-avx-elevated border border-avx-line" />
                                    )}
                                    <div>
                                        <h3 className="font-heading font-bold text-lg">{lic.product?.name || "—"}</h3>
                                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                                            <StatusBadge status={visuallyActive ? "active" : "cancelled"} />
                                            <span className="label-mono">{lic.active_count}/{lic.max_activations} machines</span>
                                            {isTrial && !expired && (
                                                <span
                                                    className="text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded-sm bg-avx-success/15 text-avx-success border border-avx-success/40"
                                                    data-testid={`trial-badge-${lic.id}`}
                                                >
                                                    Trial · {days != null ? `${days} day${days === 1 ? "" : "s"} left` : "5 days"}
                                                </span>
                                            )}
                                            {isTrial && expired && (
                                                <span
                                                    className="text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded-sm bg-avx-danger/15 text-avx-danger border border-avx-danger/40"
                                                    data-testid={`trial-expired-badge-${lic.id}`}
                                                >
                                                    Trial expired
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {isTrial && lic.product?.slug && (
                                <div
                                    className={`panel p-3 mb-4 ${expired ? "bg-avx-danger/10 border-avx-danger/40" : "bg-avx-blue/5 border-avx-blue/30"}`}
                                    data-testid={`trial-cta-${lic.id}`}
                                >
                                    <div className="flex items-center justify-between gap-3 flex-wrap">
                                        <div className="text-xs leading-relaxed">
                                            {expired
                                                ? <>Your <span className="font-bold">{lic.product.name}</span> trial has ended. Buy the full version to keep using it on your machines.</>
                                                : <>You are using a trial license. Buy the full version before it expires to keep your plugin active.</>}
                                        </div>
                                        <Link
                                            to={`/plugins/${lic.product.slug}`}
                                            className="btn-primary px-4 py-2 text-[10px] font-mono uppercase tracking-[0.2em] rounded-sm shrink-0"
                                            data-testid={`buy-full-${lic.id}`}
                                        >
                                            Buy full version →
                                        </Link>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-center mb-5">
                                <div className="panel p-3 font-mono text-sm tracking-wider break-all" data-testid={`license-key-${lic.id}`}>
                                    {lic.key}
                                </div>
                                <button onClick={() => copyKey(lic.key)} className="btn-secondary px-5 py-2 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid={`copy-key-${lic.id}`}>
                                    {copied === lic.key ? "Copied ✓" : "Copy"}
                                </button>
                            </div>

                            <div className="border-t border-avx-line pt-4">
                                <div className="label-mono mb-3">/ Activated machines</div>
                                {lic.activations.filter(a => !a.deactivated_at).length === 0 ? (
                                    <div className="text-xs text-avx-muted italic">No machines activated. Use the AVX Pro desktop client to activate.</div>
                                ) : (
                                    <div className="space-y-2">
                                        {lic.activations.filter(a => !a.deactivated_at).map((a) => (
                                            <div key={a.id} className="flex items-center justify-between border border-avx-line rounded-sm p-3 bg-avx-bg/40">
                                                <div>
                                                    <div className="text-sm font-mono">{a.machine_name}</div>
                                                    <div className="text-[10px] font-mono text-avx-muted uppercase tracking-[0.18em]">{a.os} · activated {formatDateTime(a.activated_at)}</div>
                                                </div>
                                                <button onClick={() => deactivate(lic.id, a.id, a.machine_name)} className="text-xs font-mono uppercase tracking-[0.18em] text-avx-danger hover:bg-avx-danger/10 px-3 py-1 rounded-sm" data-testid={`deactivate-${a.id}`}>
                                                    Liberar
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}
        </DashboardLayout>
    );
}
